/**
 * Default Banner Renderer - SIMPLE & FLEXIBLE
 * Works with any project, minimal dependencies
 */

import { SmartBannerError } from "../error/SmartBannerError.js";
import { getSize, applySize } from "../utils/sizeUtils.js";

// CDN Configuration
const CDN_PATH = "https://cdn2.enuygun.com/img/smart-banner/";

// HTML Security Utils
function escapeHtml(text) {
  if (typeof text !== "string") return "";
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function sanitizeUrl(url) {
  if (typeof url !== "string") return "#";

  // Allow only http, https, and relative URLs
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("/")
  ) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      if (parsedUrl.protocol === "http:" || parsedUrl.protocol === "https:") {
        return parsedUrl.href;
      }
    } catch (e) {
      // Invalid URL
    }
  }
  return "#";
}

export const DefaultRenderer = {
  /**
   * Create HTML for a single banner - NEW SCHEMA COMPATIBLE
   */
  createElement(banner, props = {}) {
    if (!banner) {
      throw SmartBannerError.renderError("Banner data is required");
    }

    const imageUrl = this._getImageUrl(banner);
    const imageAlt = escapeHtml(
      banner.alt ||
        banner.images?.[0]?.alt ||
        banner.images?.[0]?.img?.alternativeText ||
        banner.title ||
        "Smart Banner"
    );

    // NEW: Use proper click URL and attributes from new schema
    const url = banner.url || banner.images?.[0]?.clickUrl || "";
    const target = escapeHtml(
      banner.target || banner.images?.[0]?.target || "_blank"
    );
    const rel = escapeHtml(
      banner.rel || banner.images?.[0]?.rel || "noopener noreferrer"
    );

    // Check if URL is valid for linking
    const hasValidUrl = url && url !== "" && url !== "#";

    const imageElement = `<img src="${escapeHtml(imageUrl)}" 
             alt="${imageAlt}" 
             loading="lazy" 
             decoding="async"
             onerror="this.style.display='none'">`;

    // If no valid URL, return just the image without link
    if (!hasValidUrl) {
      return `<div class="smart-banner smart-banner-no-link">${imageElement}</div>`.trim();
    }

    // Return with link - NEW: proper attributes
    return `
      <a href="${sanitizeUrl(url)}" 
         class="smart-banner" 
         target="${target}" 
         rel="${rel}">
        ${imageElement}
      </a>
    `.trim();
  },

  /**
   * Render banner to container with inventory-based sizing
   */
  renderWithInventory(
    bannerData,
    container,
    props,
    inventory = "PRODUCT_HOMEPAGE"
  ) {
    if (!container || !(container instanceof HTMLElement)) {
      throw SmartBannerError.renderError("Valid container element is required");
    }

    // Handle array or single banner
    const banners = Array.isArray(bannerData) ? bannerData : [bannerData];

    if (banners.length === 0) {
      container.innerHTML =
        '<div style="padding:20px;color:#999;">No banner available</div>';
      return container;
    }

    // Single banner rendering
    const banner = banners[0];

    // NEW: URL is already processed by index.js, no need to override
    // The banner.url already contains the correct clickUrl from the selected image

    const html = this.createElement(banner, props);
    container.innerHTML = html;

    // Apply inventory-based FORCE styling
    this._forceInventoryStyles(container, inventory);

    // Add click tracking
    this._bindEvents(container, banner, props);

    return container;
  },

  /**
   * Legacy render method - uses inventory-based sizing
   * @deprecated Use renderWithInventory instead
   */
  render(bannerData, container, props) {
    const inventory = bannerData?.[0]?.inventory || "PRODUCT_HOMEPAGE";
    return this.renderWithInventory(bannerData, container, props, inventory);
  },

  /**
   * Get image URL - NEW SCHEMA COMPATIBLE
   */
  _getImageUrl(banner) {
    // Debug logging - only show if enabled globally
    const debugMode = window.SmartBanner?.config?.debug;

    if (debugMode) {
      console.log("[DefaultRenderer] _getImageUrl called with banner:", banner);
    }

    let imageUrl = null;

    // NEW: Handle new BFF response structure
    if (
      banner.images &&
      Array.isArray(banner.images) &&
      banner.images.length > 0
    ) {
      const image = banner.images[0];
      if (debugMode) {
        console.log("[DefaultRenderer] First image object:", image);
      }
      if (image.img?.url) {
        imageUrl = image.img.url;
        if (debugMode) {
          console.log("[DefaultRenderer] Found image.img.url:", imageUrl);
        }
      }
    }

    // Fallback to direct field (pre-processed by index.js)
    if (!imageUrl && banner.image) {
      imageUrl = banner.image;
      if (debugMode) {
        console.log("[DefaultRenderer] Using banner.image fallback:", imageUrl);
      }
    }

    if (debugMode) {
      console.log(
        "[DefaultRenderer] Raw imageUrl before processing:",
        imageUrl
      );
    }

    // NEW: Apply CDN path for relative URLs - HER ZAMAN CDN KULLAN
    if (imageUrl && !this._isFullUrl(imageUrl)) {
      const finalUrl = CDN_PATH + imageUrl.replace(/^\//, "");

      if (debugMode) {
        console.log("[DefaultRenderer] CDN URL:", finalUrl);
      }
      return finalUrl;
    }

    // Default placeholder
    if (!imageUrl) {
      if (debugMode) {
        console.log("[DefaultRenderer] No imageUrl found, using placeholder");
      }
      return 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="360" height="100"%3E%3Crect width="360" height="100" fill="%23f0f0f0"/%3E%3Ctext x="180" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';
    }

    if (debugMode) {
      console.log("[DefaultRenderer] Returning imageUrl as-is:", imageUrl);
    }
    return imageUrl;
  },

  /**
   * FORCE inventory styles with CSS + inline styles
   */
  _forceInventoryStyles(container, inventory) {
    const isMobile = window.innerWidth <= 768;
    let containerHeight;

    switch (inventory) {
      case "PRODUCT_HOMEPAGE":
        containerHeight = isMobile ? "250px" : "150px";
        break;
      case "WAITING_AREA":
        containerHeight = isMobile ? "200px" : "420px";
        break;
      default:
        containerHeight = isMobile ? "250px" : "150px";
    }

    // CSS-based forcing
    const forceStyleId = `force-inventory-${inventory}-${Date.now()}`;
    const forceCSS = `
      <style id="${forceStyleId}">
        [data-smart-banner] * .smart-banner,
        .smart-banner {
          width: 100% !important;
          height: ${containerHeight} !important;
          display: block !important;
          overflow: hidden !important;
        }
        
        [data-smart-banner] * .smart-banner img,
        .smart-banner img {
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          display: block !important;
          max-width: none !important;
          max-height: none !important;
        }
        
        [data-smart-banner] {
          width: 100% !important;
          height: ${containerHeight} !important;
          display: block !important;
          overflow: hidden !important;
        }
      </style>
    `;

    document.head.insertAdjacentHTML("beforeend", forceCSS);

    // Additional inline force on all elements
    const bannerElements = container.querySelectorAll(".smart-banner");
    bannerElements.forEach((bannerEl) => {
      bannerEl.style.cssText = `
        width: 100% !important;
        height: ${containerHeight} !important;
        display: block !important;
        overflow: hidden !important;
      `;

      const img = bannerEl.querySelector("img");
      if (img) {
        img.style.cssText = `
          width: 100% !important;
          height: 100% !important;
          object-fit: cover !important;
          display: block !important;
          max-width: none !important;
          max-height: none !important;
        `;
      }
    });

    if (window.SmartBanner?.config?.debug) {
      console.log(`[DefaultRenderer] FORCE applied styles for ${inventory}:`, {
        containerHeight,
        isMobile,
        bannerElements: bannerElements.length,
      });
    }
  },

  /**
   * Check if URL is full URL
   */
  _isFullUrl(url) {
    return (
      typeof url === "string" &&
      (url.startsWith("http://") ||
        url.startsWith("https://") ||
        url.startsWith("data:"))
    );
  },

  /**
   * Sanitize URL
   */
  _sanitizeUrl(url) {
    if (typeof url !== "string") return "#";

    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.protocol === "http:" || parsed.protocol === "https:") {
        return parsed.href;
      }
    } catch (e) {
      // Invalid URL
    }

    return "#";
  },

  /**
   * Add event listeners - SIMPLE
   */
  _bindEvents(container, banner, props) {
    const linkElement = container.querySelector(".smart-banner");
    if (!linkElement) return;

    // Click tracking
    linkElement.addEventListener("click", () => {
      if (window.gtag) {
        window.gtag("event", "smart_banner_click", {
          banner_id: banner.id,
          banner_place: props.place,
          banner_domain: props.domain,
        });
      }
    });
  },
};
